import { createContext, forwardRef, useContext, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../lib/cn.ts'

interface TabsContextValue {
  value: string
  onValueChange: (value: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

function useTabs() {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('Tabs primitives must be rendered inside <Tabs>')
  return ctx
}

interface TabsProps {
  value: string
  onValueChange: (value: string) => void
  children: ReactNode
  className?: string
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  { value, onValueChange, children, className },
  ref,
) {
  return (
    <TabsContext.Provider value={{ value, onValueChange }}>
      <div ref={ref} className={className}>
        {children}
      </div>
    </TabsContext.Provider>
  )
})

export const TabsList = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function TabsList({ children, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        role="tablist"
        className={cn(
          'inline-flex h-10 max-w-full items-center overflow-x-auto overflow-y-hidden rounded-md border border-border bg-background-secondary p-1',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },
)

interface TabsTriggerProps {
  value: string
  children: ReactNode
  className?: string
  /** When true, the trigger renders inert (no click handler, native `disabled`). */
  disabled?: boolean
}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(function TabsTrigger(
  { value, children, className, disabled },
  ref,
) {
  const ctx = useTabs()
  const isActive = ctx.value === value
  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      aria-selected={isActive}
      disabled={disabled}
      onClick={disabled ? undefined : () => ctx.onValueChange(value)}
      className={cn(
        'inline-flex h-8 items-center justify-center whitespace-nowrap rounded-sm px-3.5 text-sm font-medium transition-colors',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        isActive
          ? 'bg-primary/20 text-foreground ring-1 ring-inset ring-primary/40 shadow-sm'
          : 'text-muted hover:text-foreground',
        disabled && 'cursor-not-allowed text-muted/50 hover:text-muted/50',
        className,
      )}
    >
      {children}
    </button>
  )
})

interface TabsContentProps {
  value: string
  children: ReactNode
  className?: string
}

export const TabsContent = forwardRef<HTMLDivElement, TabsContentProps>(function TabsContent(
  { value, children, className },
  ref,
) {
  const ctx = useTabs()
  if (ctx.value !== value) return null
  return (
    <div ref={ref} role="tabpanel" className={className}>
      {children}
    </div>
  )
})
